// Text rendering for node cache canvas

import { drawText as canvasDrawText } from '@/services/canvas/text/CanvasTextService';
import type { TitleBounds } from '@/modules/Node';

// Cache font color RGB components to avoid regex parsing on every render
import useColorUtils from '@/utils/colorUtils';
const { hexToRGB, RGB_to_hex } = useColorUtils();
const _fontRGBCache = new Map<string, string>();

function getFontFillStyle(fontColor: string | null | undefined, globalAlpha: number): string {
  // `== null` covers both null and undefined — minimal node designs may omit font_color
  if (fontColor == null) return 'rgba(0, 0, 0, 1)';
  if (fontColor.includes('rgba')) return fontColor;

  let rgbBase = _fontRGBCache.get(fontColor);
  if (!rgbBase) {
    const rgb = fontColor.includes('rgb') ? hexToRGB(RGB_to_hex(fontColor)) : hexToRGB(fontColor);
    rgbBase = `${rgb.r}, ${rgb.g}, ${rgb.b}`;
    _fontRGBCache.set(fontColor, rgbBase);
  }
  return `rgba(${rgbBase}, ${globalAlpha})`;
}

interface TextRenderState {
  title: string | null;
  fontSize: number;
  fontFamily: string;
  fontWeight: string;
  fontStyle: string;
  fontColor: string | null | undefined;
  lineHeight: number;
  border: boolean;
  borderWidth: number;
  width: number;
  height: number;
  xCache: number | null;
  yCache: number | null;
  globalAlpha: number;
  titleBounds: TitleBounds | null;
}

export function drawText(ctx: CanvasRenderingContext2D, state: TextRenderState): void {
  if (state.fontSize === 0) return;
  if (!state.title) return;

  const computedBorder = state.border ? state.borderWidth : 0;
  const { x: titleX, y: titleY, width: titleW, height: titleH, align: tAlign } = state.titleBounds!;

  ctx.save();
  ctx.fillStyle = getFontFillStyle(state.fontColor, state.globalAlpha);

  canvasDrawText(
    ctx,
    state.title,
    {
      x: titleX! + state.xCache! - state.width / 2 - computedBorder,
      y: titleY! + state.yCache! - state.height / 2 - computedBorder,
      width: titleW,
      height: titleH,
    },
    {
      fontSize: state.fontSize,
      fontFamily: state.fontFamily,
      fontWeight: state.fontWeight,
      fontStyle: state.fontStyle,
      lineHeight: state.fontSize * state.lineHeight,
    },
    { align: tAlign ?? 'center', vAlign: 'center' },
  );

  ctx.restore();
}

export type { TextRenderState };
